/*global swal*/
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SweetAlert from 'react-swal';

import { loadProviderPracticePrograms, addProviderPracticeProgram, updateProviderPracticeProgram, deleteProviderPracticeProgram } from './api';
import AttributeGroupForm from './AttributeGroupForm';
import AttributeGroupList from './AttributeGroupList';

const AttributeGroup = ({ user, showNotification }) => {
	const [attributeGroups, setAttributeGroups] = useState([]);
	const [selectedAttributeGroup, setSelectedAttributeGroup] = useState({});
	const [showSwal, setShowSwal] = useState(false);
	const [attributeGroupToDelete, setAttributeGroupToDelete] = useState();

	useEffect(() => {
		if (!showSwal) {
			swal.close();
		}
	}, [showSwal]);

	useEffect(() => {
		setSelectedAttributeGroup({ providerPracticeId: user.provider_practice_id });
		loadProviderPracticePrograms(user.provider_practice_id)
			.then(({ data }) => {
				setAttributeGroups(data);
			})
			.catch((/* err */) => {
				showNotification({
					status: 'danger',
					message: 'Unable to load Practice Programs!'
				});
			});
	}, [user]);

	const handleConfirmDeleteAttributeGroup = (attributeGroup) => {
		setAttributeGroupToDelete(attributeGroup);
		setShowSwal(true);
	};

	const handleDeleteAttributeGroup = (isConfirm) => {
		if (isConfirm) {
			deleteProviderPracticeProgram({ providerPracticeProgramId: attributeGroupToDelete.id, providerId: user.id })
				.then(() => {
					const agIndex = attributeGroups.findIndex(ag => ag.id === attributeGroupToDelete.id);
					if (agIndex >= 0) {
						const newAttributeGroups = attributeGroups.slice();
						newAttributeGroups.splice(agIndex, 1);
						setAttributeGroups(newAttributeGroups);
					}
					showNotification({
						status: 'success',
						message: 'Program deleted successfully'
					});
				})
				.catch(() => {
					showNotification({
						status: 'danger',
						message: 'Program not deleted!'
					});
				});
		}
		setSelectedAttributeGroup({ providerPracticeId: user.provider_practice_id });
		setAttributeGroupToDelete(null);
		setShowSwal(false);
	};

	const handleSaveAttributeGroup = (attributeGroup) => {
		if (attributeGroup.id) {
			const agIndex = attributeGroups.findIndex(ag => ag.id === attributeGroup.id);
			updateProviderPracticeProgram(attributeGroup)
				.then(({ data }) => {
					const newAttributeGroups = attributeGroups.slice();
					newAttributeGroups[agIndex] = data;
					setAttributeGroups(newAttributeGroups);
					setSelectedAttributeGroup({ providerPracticeId: user.provider_practice_id });
					showNotification({
						status: 'success',
						message: 'Program updated successfully'
					});
				})
				.catch(() => {
					showNotification({
						status: 'danger',
						message: 'Unable to update Practice Program! Please try again.'
					});
				});
		} else {
			addProviderPracticeProgram(attributeGroup)
				.then(({ data }) => {
					// add to end of list
					const newAttributeGroups = attributeGroups.slice();
					newAttributeGroups.push(data);
					setAttributeGroups(newAttributeGroups);
					setSelectedAttributeGroup({ providerPracticeId: user.provider_practice_id });
					showNotification({
						status: 'success',
						message: 'Program added successfully'
					});
				})
				.catch(() => {
					showNotification({
						status: 'danger',
						message: 'Unable to add Practice Program! Please try again.'
					});
				});
		}
	};

	return (
		<React.Fragment>
			<SweetAlert
				isOpen={showSwal}
				type="warning"
				title="Are you sure?"
				text="Delete this Program?"
				showCancelButton
				confirmButtonColor="#DD6B55"
				confirmButtonText="Yes"
				closeOnConfirm={false}
				closeOnCancel={false}
				callback={handleDeleteAttributeGroup}
			/>
			<div className="attribute-group-form panel panel-default">
				<div className="panel-body">
					<AttributeGroupForm
						attributeGroup={selectedAttributeGroup}
						onReset={() => setSelectedAttributeGroup({ providerPracticeId: user.provider_practice_id })}
						onSave={handleSaveAttributeGroup}
					/>
				</div>
			</div>
			<div className="attribute-group-list panel panel-default">
				<div className="panel-body">
					<AttributeGroupList
						attributeGroups={attributeGroups}
						onEdit={attributeGroup => setSelectedAttributeGroup(attributeGroup)}
						onDelete={attributeGroup => handleConfirmDeleteAttributeGroup(attributeGroup)}
					/>
				</div>
			</div>
		</React.Fragment>
	);
};

AttributeGroup.propTypes = {
	user: PropTypes.object.isRequired,
	showNotification: PropTypes.func.isRequired
};

export default AttributeGroup;
